import React, {useContext, useState, useEffect, useCallback, useMemo, useRef} from "react";
import {useParams} from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Contexts
import {useSocket} from "../../contexts/socket.js";
import {AuthContext} from "../../contexts/auth.js";
import {NotificationContext} from "../../contexts/notify.js";
import {SettingsContext} from "../../contexts/settings.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import ChecksheetDue from "./ChecksheetDue.js";
import Accordion from "../../components/Accordion.js";

// Style
import {voice} from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {Bullet, HeadingMedium, Loader, NotLoaded} from "../../style/components/general.js";

// Socket Constants
import {
  LISTEN_NOTIFY_FREQUENCY_CHANGE,
  LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE,
  LISTEN_NOTIFY_RESPONSES,
  REQUEST_SHARED_FIELDS,
  LISTEN_NOTIFY_DRAFT,
  LISTEN_NOTIFY_COMPLETED
} from "../general/Room.js";

const ChecksheetsDue = ({room, locks, setHasUpdates, refreshingNotifications, handleUpdates}) => {
  const isMounted = useMountedState();

  const {slug} = useParams();

  const socket = useSocket();

  const {settings} = useContext(SettingsContext);

  const {roleCanAccessResource} = useContext(AuthContext);

  const {notifications, updateChecksheetInfo} = useContext(NotificationContext);

  const [hide, setHide] = useState([]);
  const [completing, setCompleting] = useState();
  const [sharedResponses, setSharedResponses] = useState();
  const [optionalOpen, setOptionalOpen] = useState(false);
  // Set while a lazy Complete GET is in flight so socket refreshes do not wipe the open.
  const openingIdRef = useRef(null);
  const completingRef = useRef(completing);
  completingRef.current = completing;

  const {api: apiChecksheets} = useApi("checksheets");

  useEffect(() => {
    if (refreshingNotifications.current && notifications) {
      refreshingNotifications.current = false;
      setHide([]);
      // Preserve an open or mid-open form; refreshing while completing/opening is skipped
      // by socket handlers, but a refresh started just before open must not clear it.
      if (!completingRef.current && !openingIdRef.current) setCompleting(undefined);
    }
  }, [notifications, refreshingNotifications]);

  const listSharedResponses = useCallback(responses => setSharedResponses(responses), []);

  useEffect(() => {
    if (isMounted() && slug) socket.emit(REQUEST_SHARED_FIELDS, slug);

    socket.on(LISTEN_NOTIFY_RESPONSES, listSharedResponses);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LISTEN_NOTIFY_RESPONSES, listSharedResponses);
    };
  }, [isMounted, socket, slug, listSharedResponses]);

  const shouldDeferRefresh = useCallback(
    () => Boolean(completing || openingIdRef.current),
    [completing]
  );

  const notifyFrequencyChange = useCallback(
    (payload = {}) => {
      const {checksheetId, checksheetIds} = payload;
      const ids =
        checksheetIds?.map(id => `${id}`) ?? (checksheetId != null ? [`${checksheetId}`] : []);

      if (!ids.length) {
        if (!shouldDeferRefresh()) handleUpdates();
        else setHasUpdates(true);
        return;
      }

      ids.forEach(id => {
        if (
          !optionalOpen &&
          notifications?.checksheetsOptional?.map(({id: optionalId}) => `${optionalId}`).includes(id)
        ) {
          apiChecksheets.callGet(id).then(response => {
            if (response?.status === 200 && response?.data?.frequency)
              setHide(prev => [...prev, `${response.data.id}`]);
          });
        } else {
          apiChecksheets
            .callGet(id, {withDrafts: true, withCurrentState: true})
            .then(response => {
              if (response?.status === 200 && response?.data) {
                const {data} = response;
                if (!data.frequency || !data.available) setHide(prev => [...prev, `${data.id}`]);
                if (!data.frequency) setHasUpdates(true);

                updateChecksheetInfo(data.id, data);
              }
            });
        }
      });

      if (!shouldDeferRefresh()) handleUpdates();
      else setHasUpdates(true);
    },
    [
      notifications,
      optionalOpen,
      apiChecksheets,
      setHide,
      setHasUpdates,
      updateChecksheetInfo,
      handleUpdates,
      shouldDeferRefresh
    ]
  );

  const notifyChecksheetOptionalChange = useCallback(() => {
    if (!shouldDeferRefresh()) handleUpdates();
    else setHasUpdates(true);
  }, [setHasUpdates, handleUpdates, shouldDeferRefresh]);

  useEffect(() => {
    socket.on(LISTEN_NOTIFY_FREQUENCY_CHANGE, notifyFrequencyChange);
    socket.on(LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE, notifyChecksheetOptionalChange);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LISTEN_NOTIFY_FREQUENCY_CHANGE, notifyFrequencyChange);
      socket.off(LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE, notifyChecksheetOptionalChange);
    };
  }, [socket, notifyFrequencyChange, notifyChecksheetOptionalChange]);

  const notifyDraft = useCallback(
    ({resourceKey}) => {
      if (resourceKey) {
        const [type, resourceId] = resourceKey.split("_");

        if (type === "checksheet")
          apiChecksheets.callGet(resourceId).then(({status, data}) => {
            if (status === 200 && data?.recordDraft) {
              updateChecksheetInfo(Number(resourceId), data.recordDraft, "recordDraft");
            }
          });
      }
    },
    [apiChecksheets, updateChecksheetInfo]
  );

  const notifyCompleted = useCallback(
    ({resourceKey, shared}) => {
      if (resourceKey) {
        const [type, resourceId] = resourceKey.split("_");

        const isOptionalId = notifications?.checksheetsOptional
          ?.map(({id}) => `${id}`)
          .includes(resourceId);
        if (!isOptionalId) {
          setHide(prev => (!prev.includes(resourceId) ? [...prev, resourceId] : prev));
        }
        setSharedResponses(shared);

        if (type === "checksheet" && completing?.id && resourceId === `${completing.id}`) {
          updateChecksheetInfo(completing?.id, null, "recordDraft");
        }
      }
    },
    [completing, notifications, setHide, updateChecksheetInfo]
  );

  // Socket Management
  useEffect(() => {
    socket.on(LISTEN_NOTIFY_DRAFT, notifyDraft);
    socket.on(LISTEN_NOTIFY_COMPLETED, notifyCompleted);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LISTEN_NOTIFY_DRAFT, notifyDraft);
      socket.off(LISTEN_NOTIFY_COMPLETED, notifyCompleted);
    };
  }, [socket, notifyCompleted, notifyDraft]);

  const checksheetsDueCount = useMemo(
    () =>
      notifications?.checksheetsDue?.filter(checksheet => !hide.includes(`${checksheet.id}`))
        ?.length || 0,
    [notifications, hide]
  );

  return (
    <Checksheets>
      {locks && notifications?.checksheetsDue ? (
        <>
          <Glance>
            {checksheetsDueCount > 0 && (
              <>
                <HeadingMedium>
                  {checksheetsDueCount} Checksheet
                  {checksheetsDueCount > 1 ? "s" : ""} Upcoming
                </HeadingMedium>

                {notifications.checksheetsDue
                  .filter(checksheet => !hide.includes(`${checksheet.id}`))
                  .map(checksheet => (
                    <ChecksheetDue
                      key={`${checksheet.facility.slug}-${checksheet.id}`}
                      room={room}
                      locks={locks}
                      checksheet={checksheet}
                      sharedResponses={sharedResponses}
                      setSharedResponses={setSharedResponses}
                      completing={completing}
                      setCompleting={setCompleting}
                      setHide={setHide}
                      openingIdRef={openingIdRef}
                    />
                  ))}
              </>
            )}

            {(notifications.totalDueCount === 0 || checksheetsDueCount === 0) && (
              <MessageWrapper>
                <HeadingMedium>Checksheets</HeadingMedium>
                <Bullet>
                  {notifications.hasChecksheets
                    ? "You are all caught up!"
                    : "Create a checksheet or event to get started!"}
                </Bullet>
              </MessageWrapper>
            )}
          </Glance>

          {notifications.hasExternalChecksheets &&
            notifications?.checksheetsExternal?.length > 0 && (
              <Glance>
                <GlanceHeading>
                  {notifications.checksheetsExternal.length} External Checksheet
                  {notifications.checksheetsExternal.length > 1 ? "s" : ""} Upcoming
                </GlanceHeading>
                {notifications.checksheetsExternal.map(checksheet => (
                  <Bullet key={checksheet.id}>
                    {checksheet.name} is available and due{" "}
                    {prettyDateInUserTimezone(checksheet.dateDue, settings.timezone)}, but only
                    available via email.
                  </Bullet>
                ))}
              </Glance>
            )}

          {roleCanAccessResource("facility_checksheet_record", "create") &&
            notifications?.checksheetsOptional?.filter(({id}) => !hide.includes(`${id}`))?.length >
              0 && (
              <Accordion
                label="Optional Checksheets"
                handleOpen={() => setOptionalOpen(prev => !prev)}
                toggleOpen={optionalOpen}>
                <Glance>
                  {notifications.checksheetsOptional
                    .filter(checksheet => !hide.includes(`${checksheet.id}`))
                    .map(checksheet => (
                      <ChecksheetDue
                        key={`${checksheet.facility.slug}-${checksheet.id}`}
                        room={room}
                        locks={locks}
                        checksheet={checksheet}
                        sharedResponses={sharedResponses}
                        setSharedResponses={setSharedResponses}
                        completing={completing}
                        setCompleting={setCompleting}
                        setHide={setHide}
                        openingIdRef={openingIdRef}
                      />
                    ))}
                </Glance>
              </Accordion>
            )}
        </>
      ) : (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}
    </Checksheets>
  );
};

ChecksheetsDue.propTypes = {
  room: PropTypes.string.isRequired,
  locks: PropTypes.objectOf(PropTypes.any),
  setHasUpdates: PropTypes.func.isRequired,
  refreshingNotifications: PropTypes.objectOf(PropTypes.bool).isRequired,
  handleUpdates: PropTypes.func.isRequired
};

// Style Overrides
const Checksheets = styled.div`
  position: relative;
  height: 100%;
`;

const MessageWrapper = styled.div`
  padding-top: ${pad * 3}px;
`;

const Glance = styled.div`
  ${flex("column", "nowrap")};
  gap: ${pad}px;
  margin-bottom: ${pad}px;
  width: 100%;
`;

const GlanceHeading = styled.h2`
  ${voice.strong};
  font-weight: bold;
  padding: ${pad}px 0;
  color: ${({theme}) => theme.secondary};
`;

export default ChecksheetsDue;
